import React, { useState } from 'react';
import styled from 'styled-components';
import { Input } from '../../components/shared/Input';
import Button from '../../components/shared/Button';
import { gql, useQuery, useMutation } from '@apollo/client';
import { getServiceTag } from '../../lib/getServiceTag';

interface System {
  serviceTag: string;
  pin: string;
  id: string;
}

const GET_USER_SYSTEMS = gql`
  query UserSystems {
    UserSystems {
      serviceTag
      pin
      id
    }
  }
`;

const ADD_SYSTEM = gql`
  mutation AddSystem($serviceTag: String!, $pin: String!) {
    AddSystem(serviceTag: $serviceTag, pin: $pin) {
      serviceTag
      pin
    }
  }
`;

const DELETE_SYSTEM = gql`
  mutation DeleteUserSystem($id: ID!) {
    DeleteUserSystem(id: $id)
  }
`;

const ErrorMessage = styled.div`
  color: red;
  margin-top: 8px;
  font-size: 14px;
`;

const SystemContainer = styled.div`
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 16px;
`;

const PinDisplay = styled.span`
  font-family: monospace;
  letter-spacing: 2px;
`;

const SystemHeader = styled.h3`
  margin: 0 0 8px 0;
  font-size: 18px;
`;

const DeleteButton = styled.button`
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 4px;
  margin-left: 8px;
  &:hover {
    color: red;
  }
`;

const Systems: React.FC = () => {
  const [newServiceTag, setNewServiceTag] = useState(getServiceTag());
  const [newPinCode, setNewPinCode] = useState('');
  const [error, setError] = useState('');

  const { loading, error: queryError, data } = useQuery(GET_USER_SYSTEMS);
  const [addSystem] = useMutation(ADD_SYSTEM, {
    refetchQueries: [{ query: GET_USER_SYSTEMS }],
  });
  const [deleteSystem] = useMutation(DELETE_SYSTEM, {
    refetchQueries: [{ query: GET_USER_SYSTEMS }]
  });

  const handleAddSystem = async () => {
    if (!newServiceTag.trim()) {
      setError('Service tag is required');
      return;
    }

    if (newPinCode.length < 4) {
      setError('Pin code must be at least 4 characters long');
      return;
    }

    try {
      await addSystem({
        variables: {
          serviceTag: newServiceTag,
          pin: newPinCode
        }
      });
      setNewPinCode('');
      setError('');
    } catch (e) {
      setError('Failed to add system. Please try again.');
    }
  };

  if (loading) return <div>Loading...</div>;
  if (queryError) return <div>Error loading systems</div>;

  const systems = data?.UserSystems || [];

  return (
    <div>
      <h2>Your Systems</h2>
      
      <div style={{ marginBottom: '20px' }}>
        <Input
          type="text"
          placeholder="Service Tag"
          value={newServiceTag}
          onChange={(e) => setNewServiceTag(e.target.value)}
          disabled={true}
        />
        <Input
          type="password"
          placeholder="Pin Code"
          value={newPinCode}
          onChange={(e) => setNewPinCode(e.target.value)}
          style={{ marginTop: '10px' }}
        />
        {error && <ErrorMessage>{error}</ErrorMessage>}
        <Button onClick={handleAddSystem}>
          Add System
        </Button>
      </div>

      {systems.map((system: System, index: number) => (
        <SystemContainer key={index}>
          <SystemHeader>
            System: {system.serviceTag}
            <DeleteButton onClick={() => deleteSystem({ variables: { id: system.id }})}>
              Remove
            </DeleteButton>
          </SystemHeader>
          <div>Pin Code: <PinDisplay>{system.pin}</PinDisplay></div>
        </SystemContainer>
      ))}
    </div>
  );
};

export default Systems; 